import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Checkbox, Drawer, Grid, Input, Segmented, Skeleton, Typography, message } from 'antd';
import { ArrowLeftOutlined, AuditOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  decideScenarioVersion,
  getScenarioReview,
  getScenarioReviewQueue,
  type BatchItem,
  type ScenarioDecisionKind,
  type ScenarioReviewCase,
  type ScenarioReviewItem,
  type ScenarioReviewQueue,
} from '@/api/scenarioVersions';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { DECISION_WORDS, POLICY_WORDS, PART_WORDS, decisionQuestion, shortHash } from '../scenarioWords';
import { BatchApprovalDrawer } from './BatchApprovalDrawer';
import { ChangeList, ChecksAsRules, ControlVerdictList, ReviewRecord, RolePicker, SourcePanel, cardStyle, surface, useDeclaredRole } from './reviewParts';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// QuickReviewDrawer — Scenario Intelligence screen 05 ("Review with low effort", plan §5; D-SI-6).
//
// Decisions first: the case opens on one sentence that already says how big the change is, then "What changed?"; the
// source passage, the checks as rules and the whole scenario are one click away. The queue is grouped by the review
// policy the SERVER assigns: only "Wording only" versions may be approved together (BatchApprovalDrawer, stacked).
// Any Studio user may record an approval; identity comes from the signed-in account, the role is declared, a
// self-approval is marked for good, and a recorded approval is kept apart from clinical sign-off (_SME_REVIEW.md).
// Until the Scenario Intelligence workspace exists, this opens from the Scenarios page; the panel is the future
// Reviews tab's body.
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'open' | 'mine' | 'all';

export const QuickReviewDrawer = observer(function QuickReviewDrawer({
  open,
  onClose,
  agentId,
  agentName,
  initialScenarioId,
  onOpenScenario,
}: {
  open: boolean;
  onClose: () => void;
  agentId: number | null;
  agentName?: string;
  /** Open on this scenario's case (the workspace's Reviews tab); otherwise on the queue. */
  initialScenarioId?: string | null;
  /** "Open the whole scenario": the advanced editor. */
  onOpenScenario?: (scenarioId: string) => void;
}) {
  const { themeStore, merchantContextStore: ctx, authStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;
  const s = surface(isDark);

  const [queue, setQueue] = useState<ScenarioReviewQueue | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Map<string, BatchItem>>(new Map());
  const [batchOpen, setBatchOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const request = useRef(0);
  const merchantId = ctx.selectedMerchantId;
  const label = agentName ?? (agentId != null ? `agent #${agentId}` : 'this agent');

  useEffect(() => {
    if (!open || agentId == null) return;
    const mine = ++request.current;
    setQueueError(null);
    getScenarioReviewQueue(agentId)
      .then((q) => {
        if (mine !== request.current) return;
        setQueue(q);
        setSelectedId((id) => (id && q.items.some((i) => i.scenarioId === id) ? id : null));
      })
      .catch((e: Error) => mine === request.current && setQueueError(e.message));
    // The queue is read at the practice the browser has selected; a switch reloads it (MOD-G1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId, merchantId, reload]);

  useEffect(() => {
    if (!open) {
      setQueue(null);
      setTicked(new Map());
      setSelectedId(null);
    } else if (initialScenarioId) {
      setSelectedId(initialScenarioId);
    }
  }, [open, initialScenarioId]);

  const items = useMemo(() => {
    const all = queue?.items ?? [];
    if (tab === 'open') return all.filter((i) => i.decision == null);
    if (tab === 'mine') return all.filter((i) => i.decidedByViewer);
    return all;
  }, [queue, tab]);
  const openCount = queue?.items.filter((i) => i.decision == null).length ?? 0;
  // At desktop width the first open case is shown until another is chosen; on a phone the queue comes first.
  const selected = queue?.items.find((i) => i.scenarioId === selectedId)
    ?? (narrow ? null : queue?.items.find((i) => i.decision == null) ?? null);
  const refresh = useCallback(() => setReload((n) => n + 1), []);

  const toggle = (i: ScenarioReviewItem, on: boolean) =>
    setTicked((prev) => {
      const next = new Map(prev);
      if (on) next.set(i.scenarioId, { scenarioId: i.scenarioId, snapshotId: i.current.snapshotId, bodyHash: i.current.bodyHash });
      else next.delete(i.scenarioId);
      return next;
    });

  const queuePane = (
    <QueuePane
      items={items}
      tab={tab}
      onTab={setTab}
      openCount={openCount}
      selectedId={selected?.scenarioId ?? null}
      onSelect={setSelectedId}
      ticked={ticked}
      onToggle={toggle}
      onBatch={() => setBatchOpen(true)}
      isDark={isDark}
    />
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(1280px, 100vw)"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <AuditOutlined style={{ color: colors.violet[600] }} />
          <div style={{ minWidth: 0 }}>
            <div>Review scenarios</div>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{label} · decide first, read more only if needed</Text>
          </div>
        </div>
      }
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={refresh} aria-label="Reload the queue" />}
      styles={{ body: { padding: 0, background: s.page, display: 'flex', minHeight: 0 } }}
    >
      {agentId == null ? (
        <div style={{ padding: 20, flex: 1 }}>
          <Alert type="info" showIcon title="Pick an agent first" description="The review queue lists one agent’s scenarios. Choose the agent in the Scenarios filter bar." />
        </div>
      ) : queueError ? (
        <div style={{ padding: 20, flex: 1 }}>
          <Alert type="error" showIcon title="Could not load the review queue" description={queueError} action={<Button size="small" onClick={refresh}>Retry</Button>} />
        </div>
      ) : !queue ? (
        <div style={{ padding: 20, flex: 1 }} aria-busy="true">
          <Text strong style={{ display: 'block' }}>Loading the review queue for {label}</Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12.5 }}>Each scenario’s current version, what changed and who wrote it.</Text>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : queue.items.length === 0 ? (
        <div style={{ flex: 1, padding: 20 }}>
          <BrandedEmpty icon={<AuditOutlined />} title={`No scenarios for ${label}`} description="Scenarios added to the library appear here when someone saves them." />
        </div>
      ) : narrow ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {selected ? (
            <div style={{ padding: 12 }}>
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => setSelectedId(null)} style={{ paddingLeft: 0 }}>Back to the queue</Button>
              <ReviewCasePane agentId={agentId} item={selected} viewerName={authStore.user?.displayName} isDark={isDark}
                onDecided={refresh} onBack={() => setSelectedId(null)} onOpenScenario={onOpenScenario} />
            </div>
          ) : (
            queuePane
          )}
        </div>
      ) : (
        <>
          <div style={{ width: 360, flexShrink: 0, borderRight: `1px solid ${s.line}`, overflowY: 'auto', background: s.card }}>{queuePane}</div>
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: 16 }}>
            {selected ? (
              <ReviewCasePane agentId={agentId} item={selected} viewerName={authStore.user?.displayName} isDark={isDark}
                onDecided={refresh} onBack={() => setSelectedId(null)} onOpenScenario={onOpenScenario} />
            ) : openCount === 0 && tab === 'open' ? (
              <BrandedEmpty icon={<CheckOutlined />} title={`Nothing is open for ${label}`}
                description="New versions and proposals added to the library appear here when someone saves them."
                action={<Button onClick={() => setTab('mine')}>Decided by me</Button>} />
            ) : (
              <Text type="secondary">Choose a scenario in the queue.</Text>
            )}
          </div>
        </>
      )}

      <BatchApprovalDrawer
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        agentId={agentId}
        ticked={[...ticked.values()]}
        queue={queue}
        viewerName={authStore.user?.displayName}
        onOpenItem={(id) => {
          setBatchOpen(false);
          setSelectedId(id);
        }}
        onRecorded={() => {
          setBatchOpen(false);
          setTicked(new Map());
          refresh();
        }}
      />
    </Drawer>
  );
});

// ── The queue ────────────────────────────────────────────────────────────────

function QueuePane({
  items,
  tab,
  onTab,
  openCount,
  selectedId,
  onSelect,
  ticked,
  onToggle,
  onBatch,
  isDark,
}: {
  items: ScenarioReviewItem[];
  tab: Tab;
  onTab: (t: Tab) => void;
  openCount: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  ticked: Map<string, BatchItem>;
  onToggle: (i: ScenarioReviewItem, on: boolean) => void;
  onBatch: () => void;
  isDark: boolean;
}) {
  const s = surface(isDark);
  // Grouped by the policy that applies, because only items under the same policy can be approved together.
  const groups: Array<{ key: string; title: string; hint: string; tone: 'amber' | 'violet'; rows: ScenarioReviewItem[] }> = [
    { key: 'one', title: 'One at a time', hint: 'checks, context or what the user says changed, or never approved', tone: 'amber', rows: items.filter((i) => i.policy !== 'WordingOnly') },
    { key: 'wording', title: 'Wording only', hint: 'batch allowed', tone: 'violet', rows: items.filter((i) => i.policy === 'WordingOnly') },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }} aria-label="Review queue">
      <div style={{ padding: 12, borderBottom: `1px solid ${s.line}` }}>
        <Segmented<Tab>
          block
          size="small"
          value={tab}
          onChange={onTab}
          options={[
            { value: 'open', label: `Open · ${openCount}` },
            { value: 'mine', label: 'Decided by me' },
            { value: 'all', label: 'All' },
          ]}
        />
      </div>
      <div style={{ flex: 1, padding: 8 }}>
        {items.length === 0 && <Text type="secondary" style={{ display: 'block', padding: 12 }}>Nothing here.</Text>}
        {groups.filter((g) => g.rows.length > 0).map((g) => (
          <div key={g.key} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 6px 4px' }}>
              <StatusPill tone={g.tone}>{g.title}</StatusPill>
              <Text type="secondary" style={{ fontSize: 11.5 }}>{g.hint} · {g.rows.length}</Text>
            </div>
            {g.rows.map((i) => (
              <QueueRow key={i.scenarioId} item={i} selected={i.scenarioId === selectedId} ticked={ticked.has(i.scenarioId)}
                onSelect={() => onSelect(i.scenarioId)} onToggle={(on) => onToggle(i, on)} isDark={isDark} />
            ))}
          </div>
        ))}
      </div>
      <div style={{ position: 'sticky', bottom: 0, padding: 12, borderTop: `1px solid ${s.line}`, background: s.card }}>
        <Button block disabled={ticked.size === 0} onClick={onBatch}>
          {ticked.size === 0 ? 'Approve selected…' : `Approve selected (${ticked.size})…`}
        </Button>
      </div>
    </div>
  );
}

function QueueRow({ item, selected, ticked, onSelect, onToggle, isDark }: {
  item: ScenarioReviewItem; selected: boolean; ticked: boolean; onSelect: () => void; onToggle: (on: boolean) => void; isDark: boolean;
}) {
  const s = surface(isDark);
  const meta = item.decision
    ? DECISION_WORDS[item.decision].label.toLowerCase()
    : item.policy === 'Material'
      ? `${item.changedParts.map((p) => PART_WORDS[p]).join(', ') || 'changed'} changed`
      : POLICY_WORDS[item.policy].short;
  return (
    <div
      style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 8px', borderRadius: 8, cursor: 'pointer',
        background: selected ? (isDark ? 'rgba(124,58,237,.18)' : colors.violet[50]) : 'transparent',
        border: `1px solid ${selected ? colors.violet[200] : 'transparent'}`,
      }}
      onClick={onSelect}
      role="button"
      aria-pressed={selected}
      aria-label={`${item.scenarioId}, version ${item.current.number}`}
    >
      <span onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={ticked} disabled={item.decision != null} onChange={(e) => onToggle(e.target.checked)} aria-label={`Select ${item.scenarioId}`} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: s.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.scenarioId}
        </div>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          v{item.current.number} · {meta}{item.authoredByViewer ? ' · by you' : ''}
        </Text>
      </div>
    </div>
  );
}

// ── One case ─────────────────────────────────────────────────────────────────

type Panel = 'source' | 'rules' | null;

function ReviewCasePane({
  agentId,
  item,
  viewerName,
  isDark,
  onDecided,
  onBack,
  onOpenScenario,
}: {
  agentId: number;
  item: ScenarioReviewItem;
  viewerName?: string;
  isDark: boolean;
  onDecided: () => void;
  onBack: () => void;
  onOpenScenario?: (scenarioId: string) => void;
}) {
  const [data, setData] = useState<ScenarioReviewCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [role, setRole] = useDeclaredRole();
  const [note, setNote] = useState('');
  const [recording, setRecording] = useState<ScenarioDecisionKind | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    const mine = ++request.current;
    setData(null);
    setError(null);
    setPanel(null);
    setRefused(null);
    setNote('');
    getScenarioReview(agentId, item.scenarioId)
      .then((c) => mine === request.current && setData(c))
      .catch((e: Error) => mine === request.current && setError(e.message));
  }, [agentId, item.scenarioId, item.current.snapshotId]);

  const decide = async (decision: ScenarioDecisionKind) => {
    const opened = data?.item.current ?? item.current;
    setRecording(decision);
    setRefused(null);
    try {
      await decideScenarioVersion(agentId, {
        scenarioId: item.scenarioId,
        scenarioSnapshotId: opened.snapshotId,
        scenarioBodyHash: opened.bodyHash,
        decision,
        reviewerRole: role,
        justification: note.trim(),
      });
      void message.success(`Recorded: ${DECISION_WORDS[decision].label.toLowerCase()} of version ${opened.number}, bound to ${shortHash(opened.bodyHash)}.`);
      onDecided();
    } catch (e) {
      setRefused((e as Error).message);
    } finally {
      setRecording(null);
    }
  };

  if (error) return <Alert type="error" showIcon title="Could not open this scenario for review" description={error} />;
  if (!data) {
    return (
      <div aria-busy="true">
        <Text strong style={{ display: 'block' }}>Opening {item.scenarioId}, version {item.current.number}</Text>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  const it = data.item;
  const current = data.current;
  const blocked = it.blockers.length > 0;
  const canDecide = it.decision == null;
  const card = cardStyle(isDark);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-label={`Review of ${it.scenarioId}`}>
      {/* The decision leads. */}
      <section style={card} aria-label="Decision">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 600, wordBreak: 'break-all' }}>{it.scenarioId}</span>
          <StatusPill tone="slate">v{it.current.number}</StatusPill>
          <StatusPill tone="amber">Pending SME review</StatusPill>
          {it.decision && <StatusPill tone={DECISION_WORDS[it.decision].tone}>{DECISION_WORDS[it.decision].label}</StatusPill>}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, margin: '8px 0 10px' }}>
          {canDecide ? decisionQuestion(it.current.number, it.policy, it.changedParts) : `Version ${it.current.number} was decided.`}
        </div>
        {canDecide && (
          <>
            {blocked && (
              <Alert style={{ marginBottom: 10 }} type="error" showIcon title="This version cannot be approved"
                description={<ul style={{ margin: 0, paddingLeft: 18 }}>{it.blockers.map((b) => <li key={b}>{b}</li>)}</ul>} />
            )}
            {data.controls.length > 0 && <div style={{ marginBottom: 10 }}><ControlVerdictList verdicts={data.controls} isDark={isDark} /></div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button type="primary" icon={<CheckOutlined />} disabled={blocked || !!recording && recording !== 'Approved'} loading={recording === 'Approved'} onClick={() => void decide('Approved')}>
                {recording === 'Approved' ? 'Approving…' : 'Approve'}
              </Button>
              <Button disabled={!!recording && recording !== 'ChangesRequested'} loading={recording === 'ChangesRequested'} onClick={() => void decide('ChangesRequested')}>Request changes</Button>
              <Button danger disabled={!!recording && recording !== 'Rejected'} loading={recording === 'Rejected'} onClick={() => void decide('Rejected')}>Reject</Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <Text type="secondary" style={{ fontSize: 12.5 }}>Recorded as <Text strong>{viewerName ?? 'you'}</Text> (signed in) · declared role</Text>
              <RolePicker value={role} onChange={setRole} />
            </div>
            <Input.TextArea style={{ marginTop: 8 }} rows={2} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What you checked, and against which source (recorded with your decision)" aria-label="What you checked" />
            {it.authoredByViewer && (
              <Alert style={{ marginTop: 10 }} type="warning" showIcon
                title={`You saved version ${it.current.number}, so approving it is a self-approval. Studio allows it, and the permanent record labels it and shows you as both author and approver.`} />
            )}
            {refused && (
              <Alert style={{ marginTop: 10 }} type="error" showIcon title="Your decision was not recorded" description={refused}
                action={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Button size="small" onClick={onDecided}>What changed?</Button>
                    <Button size="small" onClick={onBack}>Back to the queue</Button>
                  </div>
                } />
            )}
          </>
        )}
        <Alert style={{ marginTop: 10 }} type="info" showIcon
          title={<span><b>Clinical expectation.</b> Your practice’s convention is clinician sign-off, recorded in _SME_REVIEW.md under the oncology clinical sources playbook. Studio takes your identity from the signed-in account and records the role you declare; it does not verify the role. A recorded approval is not independently verified clinical sign-off.</span>} />
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
          First-release rule (decision D-SI-6): any Studio user may approve; there is no reviewer roster. Policy-based approval authority, with
          independent review for high-consequence changes, is a proposed later change, not how Studio behaves today.
        </Text>
      </section>

      {/* Then what changed. */}
      <section style={card} aria-label="What changed">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <Text strong>What changed?</Text>
          {it.comparedWith ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              version {it.comparedWith.number}{it.comparedWithIsApproved ? ' (last approved)' : ''} → version {it.current.number}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>version {it.current.number} is the first; there is nothing to compare with</Text>
          )}
        </div>
        {data.change && current ? (
          <ChangeList change={data.change} current={current} compared={data.compared} isDark={isDark} />
        ) : current ? (
          <ChecksAsRules s={current} isDark={isDark} />
        ) : (
          <Text type="secondary">This version cannot be read.</Text>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <Button size="small" type={panel === 'source' ? 'primary' : 'default'} disabled={!current} onClick={() => setPanel(panel === 'source' ? null : 'source')}>
            {panel === 'source' ? 'Hide source' : 'Show source'}
          </Button>
          <Button size="small" type={panel === 'rules' ? 'primary' : 'default'} disabled={!current} onClick={() => setPanel(panel === 'rules' ? null : 'rules')}>
            {panel === 'rules' ? 'Hide checks' : 'Show checks as rules'}
          </Button>
          {onOpenScenario && <Button size="small" onClick={() => onOpenScenario(it.scenarioId)}>Open the whole scenario</Button>}
        </div>
      </section>

      {panel === 'source' && current && <SourcePanel s={current} isDark={isDark} />}
      {panel === 'rules' && current && <section style={card} aria-label="Checks as rules"><ChecksAsRules s={current} isDark={isDark} /></section>}

      <section style={card} aria-label="Who wrote this, who approved before">
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Who wrote this, who approved before</Text>
        <ReviewRecord versions={data.versions} decisions={data.decisions} currentNumber={it.current.number} isDark={isDark} />
      </section>
    </div>
  );
}
